import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { NOT_OBSERVED, transitionBetween, type AnchorDto, type ResponseValue } from '@genoma/shared';

import { highlightDifference } from '../../lib/highlight.ts';
import { useTranslate } from '../../i18n/index.tsx';
import { prefersReducedMotion } from '../../lib/use-gsap.ts';

type Props = {
  anchors: AnchorDto[];
  /** The step currently chosen, so the cards can echo a persistent answer. */
  selected?: number | undefined;
  /** The last mark placed; flashes the anchors it corresponds to. */
  flash?: { value: ResponseValue; nonce: number } | null;
};

const STEP_LABEL_KEYS: Record<number, string> = {
  1: 'scale.step1',
  3: 'scale.step3',
  5: 'scale.step5',
};

/** Which anchors a mark points at. A transition step points at both neighbours. */
const anchorsForValue = (value: ResponseValue): number[] => {
  if (value === NOT_OBSERVED) return [];
  const transition = transitionBetween(value);
  return transition ? [...transition] : [value];
};

/**
 * The three described steps of the ladder.
 *
 * Beyond the plain state: `selected` when the rater picked that exact step, and
 * `adjacent` when they picked 2 or 4 — a transition sits *between* two anchors,
 * so both neighbours light up softly rather than one pretending to be the
 * answer. On every mark the matching cards also flash and fade, which is what
 * ties a number on the scale back to the words it stands for. "Not observed"
 * flashes nothing: it points at no anchor at all.
 */
export const AnchorCards = ({ anchors, selected, flash }: Props) => {
  const t = useTranslate();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const overlays = container.querySelectorAll('[data-flash]');
    const steps = flash ? anchorsForValue(flash.value) : [];

    // Killing a tween leaves the element wherever it stopped, so a flash cut
    // short by moving to the next screen would carry its half-faded tint over
    // with it. Clearing first makes every render start from nothing.
    gsap.set(overlays, { opacity: 0 });
    if (steps.length === 0 || prefersReducedMotion()) return;

    const targets = steps
      .map((step) => container.querySelector(`[data-flash="${step}"]`))
      .filter((element): element is Element => Boolean(element));

    const tween = gsap.fromTo(
      targets,
      { opacity: 0.55 },
      { opacity: 0, duration: 0.9, ease: 'power2.out' },
    );
    return () => {
      tween.kill();
      gsap.set(targets, { opacity: 0 });
    };
    // The nonce is what makes an identical repeated answer flash again.
  }, [flash?.nonce, flash?.value]);

  const stateOf = (step: number): 'selected' | 'adjacent' | 'plain' => {
    if (selected === step) return 'selected';
    if (selected === 2 && (step === 1 || step === 3)) return 'adjacent';
    if (selected === 4 && (step === 3 || step === 5)) return 'adjacent';
    return 'plain';
  };

  return (
    <>
      <div className="mx-0.5 mt-4 mb-2.5 flex items-center gap-2 font-mono text-[10px] tracking-[0.13em] text-faint uppercase">
        {t('scale.heading')}
        <span className="h-px flex-1 bg-line-soft" />
      </div>

      <div ref={containerRef} className="grid gap-2.5 sm:grid-cols-3">
        {anchors.map((anchor) => {
          const state = stateOf(anchor.step);
          const others = anchors
            .filter((other) => other.step !== anchor.step)
            .map((other) => other.text);

          return (
            <div
              key={anchor.step}
              className={[
                'relative overflow-hidden rounded-xl border-[1.5px] px-3 py-2.5 transition',
                state === 'selected'
                  ? 'border-teal bg-tint-soft shadow-selected'
                  : state === 'adjacent'
                    ? 'border-[#BBD9D8] bg-tint-soft'
                    : 'border-line bg-paper',
              ].join(' ')}
            >
              {/* The flash lives on its own layer so it can fade out without
                  fighting the card's own selected / adjacent styling. */}
              <span
                data-flash={anchor.step}
                aria-hidden
                className="pointer-events-none absolute inset-0 rounded-[10px] bg-teal opacity-0"
              />

              <div className="relative mb-1.5 flex items-center gap-1.5">
                <span
                  className={[
                    'grid size-[21px] shrink-0 place-items-center rounded-full border-2 font-mono text-[10px] font-bold transition',
                    state === 'selected'
                      ? 'border-teal bg-teal text-white'
                      : state === 'adjacent'
                        ? 'border-teal text-teal'
                        : 'border-line text-soft',
                  ].join(' ')}
                >
                  {anchor.step}
                </span>
                <span
                  className={[
                    'font-mono text-[9.5px] font-bold tracking-[0.06em] uppercase',
                    state === 'selected' ? 'text-teal-deep' : 'text-soft',
                  ].join(' ')}
                >
                  {t(STEP_LABEL_KEYS[anchor.step] ?? '')}
                </span>
              </div>

              <p
                className={[
                  'relative text-[12.5px] leading-[1.36]',
                  state === 'selected' ? 'text-ink' : 'text-soft',
                ].join(' ')}
              >
                {/* The distinguishing words are computed by comparing the three
                    anchors, so nobody has to mark them by hand for 216 texts. */}
                {highlightDifference(anchor.differentiator ?? anchor.text, others).map(
                  (segment, index) =>
                    segment.distinguishing ? (
                      <strong key={index} className="font-bold text-ink">
                        {segment.text}
                      </strong>
                    ) : (
                      <span key={index}>{segment.text}</span>
                    ),
                )}
              </p>
            </div>
          );
        })}
      </div>
    </>
  );
};
